import { type Task, TaskStatus, type Waypoint } from "@/api.types.v2";
import { getTaskCareSignals } from "@/features/dispatch/selectors";
import { formatWindowRange } from "@/features/dashboard/format-window";
import { emptyLaneCounts, type LaneCounts, taskLane } from "./task-type-meta";

/** Compact amber time-window text for a task, or null. */
export function taskWindow(task: Task): string | null {
  const w = task.scheduledWindow;
  if (!w?.startTime || !w?.endTime) return null;
  return formatWindowRange(w.startTime, w.endTime);
}

export type StopStatus = "pending" | "active" | "done";

export interface DerivedStop {
  id: string;
  seq: number;
  /** Best-effort street line from the stop's tasks. */
  addr: string;
  contact: string | null;
  counts: LaneCounts;
  total: number;
  status: StopStatus;
  /** Formatted HH:MM, or null when no ETA is known. */
  eta: string | null;
  /** Amber delivery time window (from the stop's tasks), or null. */
  window: string | null;
  tasks: Task[];
  /** True if any task at this stop carries a note / fragile flag. */
  hasCare: boolean;
}

const ETA_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/** Format an ISO timestamp (or already-short string) to local HH:MM. */
export function formatEta(raw?: string | null): string | null {
  if (!raw || typeof raw !== "string") return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw; // already a display string
  return d.toLocaleTimeString([], ETA_TIME_FORMAT);
}

/** A task is DONE when it's terminally resolved — completed, failed, or
 * cancelled. Failed/cancelled tasks need no further work at the stop (retries
 * are their own tasks), so "done" counts must include them or a stop with a
 * failed pickup shows 1/2 forever. Same definition the legacy trip screens
 * and stop status use. */
export function isTaskDone(t: Pick<Task, "status">): boolean {
  return (
    t.status === TaskStatus.COMPLETED ||
    t.status === TaskStatus.FAILED ||
    t.status === TaskStatus.CANCELLED
  );
}

function stopStatus(tasks: Task[]): StopStatus {
  if (tasks.length === 0) return "pending";
  if (tasks.every(isTaskDone)) return "done";
  if (tasks.some((t) => t.status === TaskStatus.IN_PROGRESS)) return "active";
  return "pending";
}

function stopAddress(tasks: Task[]): string {
  const withAddr = tasks.find((t) => t.address?.line1);
  if (withAddr?.address) {
    const a = withAddr.address;
    return a.line2 ? `${a.line1}, ${a.line2}` : a.line1;
  }
  return "Unknown location";
}

/** Turn a trip's waypoints into ordered, display-ready stops. */
export function buildStops(waypoints: Waypoint[]): DerivedStop[] {
  return [...waypoints]
    .sort((a, b) => a.sequence - b.sequence)
    .map((wp) => {
      const tasks = wp.tasks ?? [];
      const counts = emptyLaneCounts();
      for (const t of tasks) counts[taskLane(t.type)] += 1;
      const contactTask = tasks.find((t) => t.contactName);
      const windowTask = tasks.find((t) => t.scheduledWindow);
      return {
        id: wp.id,
        seq: wp.sequence,
        addr: stopAddress(tasks),
        contact: contactTask?.contactName ?? null,
        counts,
        total: tasks.length,
        status: stopStatus(tasks),
        eta: formatEta(wp.eta?.estimatedArrivalTime),
        window: windowTask ? taskWindow(windowTask) : null,
        tasks,
        hasCare: tasks.some((t) => getTaskCareSignals(t).hasAny),
      };
    });
}

/** Aggregate lane counts across all of a trip's stops. */
export function sumLaneCounts(stops: DerivedStop[]): LaneCounts {
  const total = emptyLaneCounts();
  for (const s of stops) {
    total.pickup += s.counts.pickup;
    total.delivery += s.counts.delivery;
    total.dropoff += s.counts.dropoff;
    total.return += s.counts.return;
  }
  return total;
}

/** Index of the active stop, or the first pending one, or -1. */
export function activeStopIndex(stops: DerivedStop[]): number {
  const active = stops.findIndex((s) => s.status === "active");
  if (active >= 0) return active;
  return stops.findIndex((s) => s.status === "pending");
}

/** ETA window "08:30 – 17:15" across stops that have ETAs, or null. */
export function etaWindow(stops: DerivedStop[]): string | null {
  const etas = stops.map((s) => s.eta).filter((e): e is string => !!e);
  if (etas.length === 0) return null;
  if (etas.length === 1) return etas[0];
  return `${etas[0]} – ${etas[etas.length - 1]}`;
}
